import React, { useState, useEffect } from 'react';
import { SectionInstance } from '../types';
import { getLeadForm } from '../../../services/api';
import { Calculator, ClipboardList, ExternalLink } from 'lucide-react';

interface PricingInputRow {
  label: string;
  unit: string;
  low: string;
  high: string;
}

interface LeadFormField {
  id: string;
  type: string;
  label: string;
  placeholder?: string;
  required?: boolean;
  options?: string[];
}

interface LeadFormData {
  form_id: string;
  name: string;
  fields: LeadFormField[];
  submit_button_text?: string;
  success_message?: string;
}

const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$', AUD: '$', NZD: '$', CAD: '$', GBP: '£', EUR: '€', SGD: 'S$',
};

const PricingCalculator: React.FC<{ section: SectionInstance }> = ({ section }) => {
  const c = section.controls;
  const headline = (c.headline as string) || 'Estimate Your Cost';
  const subheadline = (c.subheadline as string) || '';
  const currency = (c.currency as string) || 'AUD';
  const formId = ((c.form_id as string) || '').trim();
  const inputDefs = (c.inputs as PricingInputRow[]) || [];
  const sym = CURRENCY_SYMBOLS[currency] || currency + ' ';

  // Quantities — one per input row, defaulting to 1
  const [quantities, setQuantities] = useState<number[]>(() => inputDefs.map(() => 1));

  // Linked lead-form data
  const [linkedForm, setLinkedForm] = useState<LeadFormData | null>(null);
  const [loadingForm, setLoadingForm] = useState(false);

  useEffect(() => {
    // Reset quantities when input list changes (e.g., schema updates in editor)
    setQuantities(inputDefs.map(() => 1));
  }, [inputDefs.length]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!formId) { setLinkedForm(null); return; }
    let cancelled = false;
    setLoadingForm(true);
    getLeadForm(formId)
      .then((data: LeadFormData) => { if (!cancelled) { setLinkedForm(data); setLoadingForm(false); } })
      .catch(() => { if (!cancelled) { setLinkedForm(null); setLoadingForm(false); } });
    return () => { cancelled = true; };
  }, [formId]);

  // Compute estimate range from current quantities
  let estLow = 0;
  let estHigh = 0;
  inputDefs.forEach((inp, i) => {
    const qty = quantities[i] ?? 1;
    estLow  += (parseFloat(inp.low  || '0') || 0) * qty;
    estHigh += (parseFloat(inp.high || '0') || 0) * qty;
  });

  const fmt = (n: number) => sym + Math.round(n).toLocaleString();

  return (
    <section style={{ padding: '80px 24px', background: 'var(--brand-bg, #fff)' }}>
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            background: 'rgba(33,119,255,0.08)', border: '1px solid rgba(33,119,255,0.2)',
            borderRadius: 6, padding: '4px 12px', marginBottom: 16,
            fontSize: '0.75rem', color: '#2177FF', fontWeight: 600,
          }}>
            <Calculator size={13} /> Pricing Calculator
          </div>
          <h2 style={{ fontSize: '2rem', fontWeight: 800, marginBottom: 8 }}>{headline}</h2>
          {subheadline && (
            <p style={{ color: 'var(--brand-text-light, #64748b)', fontSize: '1rem' }}>{subheadline}</p>
          )}
        </div>

        {/* Calculator card */}
        <div style={{
          background: 'var(--brand-bg-alt, #f8fafc)',
          borderRadius: 'var(--brand-card-radius, 12px)',
          padding: 32,
          border: '1px solid rgba(0,0,0,0.06)',
        }}>
          {inputDefs.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '32px 0', color: '#94a3b8' }}>
              <Calculator size={32} style={{ marginBottom: 12 }} />
              <p style={{ fontWeight: 600, color: '#64748b' }}>No pricing inputs configured</p>
              <p style={{ fontSize: '0.875rem', marginTop: 4 }}>
                Add pricing inputs in the Properties panel.
              </p>
            </div>
          ) : (
            <>
              {inputDefs.map((inp, i) => (
                <div key={i} style={{ marginBottom: 20 }}>
                  <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6,
                  }}>
                    <label style={{ fontWeight: 600, fontSize: '0.9375rem' }}>
                      {inp.label || `Input ${i + 1}`}
                      {inp.unit && (
                        <span style={{ fontWeight: 400, color: '#64748b', fontSize: '0.8125rem', marginLeft: 4 }}>
                          ({inp.unit})
                        </span>
                      )}
                    </label>
                    <span style={{
                      fontWeight: 700, color: 'var(--brand-primary, #1a365d)',
                      minWidth: 28, textAlign: 'right',
                    }}>
                      {quantities[i] ?? 1}
                    </span>
                  </div>
                  <input
                    type="range"
                    min={1}
                    max={20}
                    value={quantities[i] ?? 1}
                    onChange={e => {
                      const next = [...quantities];
                      next[i] = parseInt(e.target.value, 10);
                      setQuantities(next);
                    }}
                    style={{ width: '100%', accentColor: 'var(--brand-primary, #1a365d)' }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: '#94a3b8', marginTop: 2 }}>
                    <span>1</span><span>20</span>
                  </div>
                </div>
              ))}

              {/* Estimate readout */}
              <div style={{
                marginTop: 24, padding: 20,
                background: 'var(--brand-primary, #1a365d)',
                borderRadius: 8, textAlign: 'center',
              }}>
                <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.875rem', marginBottom: 4 }}>
                  Estimated cost
                </p>
                <p style={{ color: '#fff', fontSize: '1.75rem', fontWeight: 800, margin: 0 }}>
                  {estLow > 0 || estHigh > 0
                    ? `${fmt(estLow)} – ${fmt(estHigh)}`
                    : 'Enter values above'}
                </p>
                <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem', marginTop: 4 }}>
                  Indicative range — contact us for a firm quote
                </p>
              </div>
            </>
          )}
        </div>

        {/* Lead form card (editor preview — read-only) */}
        {formId && (
          <div style={{ marginTop: 40 }}>
            {loadingForm ? (
              <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: '0.875rem', padding: '32px 0' }}>
                Loading form…
              </div>
            ) : linkedForm ? (
              <div style={{
                background: 'var(--brand-bg, #fff)', padding: 32,
                borderRadius: 'var(--brand-card-radius, 12px)',
                boxShadow: '0 10px 15px -3px rgba(0,0,0,0.08), 0 4px 6px -4px rgba(0,0,0,0.04)',
              }}>
                {/* Linked form badge */}
                <div style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  background: 'rgba(30,172,166,0.08)', border: '1px solid rgba(30,172,166,0.25)',
                  borderRadius: 6, padding: '4px 10px', marginBottom: 20,
                  fontSize: '0.75rem', color: '#1EACA6', fontWeight: 600,
                }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                  </svg>
                  {linkedForm.name}
                </div>

                {linkedForm.fields.map(f => (
                  <div key={f.id} style={{ marginBottom: 12 }}>
                    <label style={{ display: 'block', fontWeight: 600, fontSize: '0.875rem', marginBottom: 4 }}>
                      {f.label}
                      {f.required && <span style={{ color: '#ef4444', marginLeft: 2 }}>*</span>}
                    </label>
                    {f.type === 'textarea' ? (
                      <textarea placeholder={f.placeholder || ''} rows={3} readOnly
                        style={{ width: '100%', padding: 10, border: '1px solid rgba(0,0,0,0.15)', borderRadius: 8, boxSizing: 'border-box', resize: 'none' }} />
                    ) : f.type === 'select' ? (
                      <select disabled style={{ width: '100%', padding: 10, border: '1px solid rgba(0,0,0,0.15)', borderRadius: 8, boxSizing: 'border-box' }}>
                        <option>Select…</option>
                        {(f.options || []).map((o, oi) => <option key={oi}>{o}</option>)}
                      </select>
                    ) : (
                      <input type={f.type || 'text'} placeholder={f.placeholder || ''} readOnly
                        style={{ width: '100%', padding: 10, border: '1px solid rgba(0,0,0,0.15)', borderRadius: 8, boxSizing: 'border-box' }} />
                    )}
                  </div>
                ))}

                <button disabled style={{
                  width: '100%', padding: '12px 24px', borderRadius: 6, border: 'none',
                  background: 'var(--brand-primary, #1a365d)', color: '#fff',
                  fontWeight: 600, cursor: 'default', marginTop: 8,
                }}>
                  {c.cta_label || linkedForm.submit_button_text || 'Get Your Free Quote'}
                </button>
              </div>
            ) : (
              /* form_id set but not found */
              <div style={{
                maxWidth: 520, margin: '0 auto', textAlign: 'center',
                border: '2px dashed #fca5a5', borderRadius: 12, padding: '32px 24px',
                color: '#ef4444',
              }}>
                <p style={{ fontWeight: 600 }}>Form not found</p>
                <p style={{ fontSize: '0.875rem', marginTop: 8 }}>
                  The selected form may have been deleted. Choose another form in the Properties panel.
                </p>
              </div>
            )}
          </div>
        )}

        {/* No form selected — prompt */}
        {!formId && (
          <div style={{
            marginTop: 32, textAlign: 'center',
            border: '2px dashed #cbd5e1', borderRadius: 12, padding: '32px 24px',
            color: '#94a3b8',
          }}>
            <ClipboardList size={28} style={{ marginBottom: 8 }} />
            <p style={{ fontWeight: 600, color: '#64748b' }}>No lead form linked</p>
            <p style={{ fontSize: '0.875rem', marginBottom: 12 }}>
              Select a Lead Capture form in the Properties panel to capture leads from calculator usage.
            </p>
            <a
              href="/lead-capture"
              target="_blank"
              rel="noreferrer"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                background: '#2177FF', color: '#fff', fontWeight: 600,
                padding: '8px 16px', borderRadius: 6, fontSize: '0.8rem',
                textDecoration: 'none',
              }}
            >
              Open Lead Capture <ExternalLink size={13} />
            </a>
          </div>
        )}
      </div>
    </section>
  );
};

export default PricingCalculator;
